import type { LC, PropsWithChildren } from '@use-gpu/live';
import type { ShaderModule } from '@use-gpu/shader';

import { provide, useContext, useMemo, useOne, useNoMemo, useNoOne, makeContext } from '@use-gpu/live';
import { bindBundle } from '@use-gpu/shader/wgsl';

import { SHADOW_PAGE } from '../render/light/light-data';

import { getLight, getLightCount } from '@use-gpu/wgsl/use/light.wgsl';
import { sampleShadow } from '@use-gpu/wgsl/use/shadow.wgsl';

import { applyLight as applyLightWGSL } from '@use-gpu/wgsl/material/light.wgsl';
import { applyLights as applyLightsWGSL } from '@use-gpu/wgsl/material/lights.wgsl';
import { applyLights as applyLightsDefaultWGSL } from '@use-gpu/wgsl/material/lights-default.wgsl';
import { applyDirectionalShadow as applyDirectionalShadowWGSL } from '@use-gpu/wgsl/shadow/directional.wgsl';
import { applyPointShadow as applyPointShadowWGSL } from '@use-gpu/wgsl/shadow/point.wgsl';
import { applyHemiShadow as applyHemiShadowWGSL } from '@use-gpu/wgsl/shadow/hemi.wgsl';
import { applySpotShadow as applySpotShadowWGSL } from '@use-gpu/wgsl/shadow/spot.wgsl';

export type LightMaterialContextProps = {
  bindApplyMaterial: (s: ShaderModule) => ShaderModule,
  useApplyMaterial: (s: ShaderModule) => ShaderModule,
  useNoApplyMaterial: () => void,
};

export const DEFAULT_LIGHT_MATERIAL_CONTEXT = {
  bindApplyMaterial: (applyMaterial: ShaderModule) => {
    const applyLight = bindBundle(applyLightWGSL, {applyMaterial});
    return bindBundle(applyLightsDefaultWGSL, {applyLight});
  },

  useApplyMaterial: (applyMaterial: ShaderModule) =>
    useOne(() => DEFAULT_LIGHT_MATERIAL_CONTEXT.bindApplyMaterial(applyMaterial), applyMaterial),

  useNoApplyMaterial: useNoOne,
};

export const LightMaterialContext = makeContext<LightMaterialContextProps>(DEFAULT_LIGHT_MATERIAL_CONTEXT, 'LightMaterialContext');

export const useLightMaterialContext = () => useContext(LightMaterialContext);

export type LightMaterialProviderProps = PropsWithChildren<{
  shadows?: boolean,
}>;

export const LightMaterialProvider: LC<LightMaterialProviderProps> = (props: LightMaterialProviderProps) => {
  const {
    shadows,
    children,
  } = props;

  const context = useOne(() => {
    const bindApplyMaterial = (applyMaterial: ShaderModule) => {

      const applyDirectionalShadow = shadows ? bindBundle(applyDirectionalShadowWGSL, {sampleShadow}) : null;
      const applyPointShadow = shadows ? bindBundle(applyPointShadowWGSL, {sampleShadow}) : null;
      const applyHemiShadow = shadows ? bindBundle(applyHemiShadowWGSL, {sampleShadow}) : null;
      const applySpotShadow = shadows ? bindBundle(applySpotShadowWGSL, {sampleShadow}) : null;

      const applyLight = bindBundle(applyLightWGSL, {
        applyMaterial,
        applyDirectionalShadow,
        applyPointShadow,
        applyHemiShadow,
        applySpotShadow,
      }, {SHADOW_PAGE});

      return bindBundle(applyLightsWGSL, {applyLight, getLightCount, getLight});
    };

    const useApplyMaterial = (applyMaterial: ShaderModule) =>
      // eslint-disable-next-line react-hooks/exhaustive-deps
      useMemo(() => bindApplyMaterial(applyMaterial), [bindApplyMaterial, applyMaterial]);

    return {bindApplyMaterial, useApplyMaterial, useNoApplyMaterial: useNoMemo};
  }, shadows);

  return (
    provide(LightMaterialContext, context, children)
  );
};
